<template>

    <transition name='vue-fade'>
        <a
            class='search-trigger'
            :class='triggerClasses'
            @click.prevent='toggleSearchDrawer'
            @keydown.enter='toggleSearchDrawer'
            tabindex='0'>
            <h6 class='search-trigger__text' v-text='"Search"'
                :class='{
                    "search-trigger__text--is-hidden": isScrolled && scrollDirection === 1
                }'
            />
            <span class='search-trigger__icon'>
                <SvgIconSearch />
            </span>
        </a>
    </transition>


    <transition name='vue-search'>
        <div v-show='isSearchDrawerOpen' class='search-drawer' data-lenis-prevent :class='drawerClasses'>

            <div class='search-drawer__content' :class='{"search-drawer__content--is-no-results": isNoResults}'>
                <div class='search-drawer__input-wrapper'>
                    <input
                        ref='searchInput'
                        type='text'
                        class='search-drawer__input'
                        placeholder='Search products...'
                        v-model='searchQuery'
                        @input='onSearchInput'
                        @keydown.esc='closeSearchDrawer'
                        autocomplete='off'
                        aria-label='Search products' />

                    <transition name='vue-fade-quick'>
                        <div v-if='isSearching' class='search-drawer__spinner'>
                            <Spinner class='search-drawer__spinner__icon' />
                        </div>
                        <a v-else-if='searchQuery' class='search-drawer__reset' @click='resetSearch' @keydown.enter='resetSearch' tabindex='0' aria-label='Reset search'>
                            <SvgIconReset class='search-drawer__reset__icon' />
                        </a>
                    </transition>

                    <a class='search-drawer__close' aria-label="Close search drawer" @click='closeSearchDrawer' @keydown.enter='closeSearchDrawer' tabindex='0'>
                        <SvgIconX/>
                    </a>
                </div>

                <transition name='vue-collapse'>
                    <div v-if='searchResults.length > 0' class='search-drawer__results' :class='{"search-drawer__results--is-searching": isSearching}'>
                        <ul class='search-drawer__results__list'>
                            <li v-for='product in searchResults' :key='product.id' class='search-drawer__result'>
                                <ProductThumb
                                    class='productthumb--horizontal'
                                    :handle='product.handle'
                                    :media='[product.featured_image]'
                                    :title='product.title'
                                    :available='product.available'
                                    :price='parseFloat(product.price) * 100'
                                    :id='product.id'
                                    :isNotShowAsSoldOut='false'
                                    @click='closeSearchDrawer' />
                            </li>
                        </ul>
                    </div>
                </transition>

                <transition name='vue-fade'>
                    <div v-if='isNoResults' class='search-drawer__empty'>
                        <span class='search-drawer__empty__text'>No products found.</span>
                    </div>
                </transition>
            </div>
        </div>
    </transition>

</template>

<script>
import { mapState } from 'pinia';
import { useScrollStore } from '^/stores/scroll';
import debounce from 'lodash/debounce'
import SvgIconSearch from '^/components/svg/SvgIconSearch.vue'
import SvgIconX from '^/components/svg/SvgIconX.vue'
import SvgIconReset from '^/components/svg/SvgIconReset.vue'
import Spinner from '^/components/elements/Spinner.vue'
import ProductThumb from '^/components/elements/ProductThumb.vue'

export default {
    name: 'Search',
    props: {
        isResetOnClose: {
            type: Boolean,
            default: false
        },
        resetSecondsAfterDrawerClosed: {
            type: [Number, Boolean],
            default: 60
        }
    },
    data () {
        return {
            isSearchDrawerOpen: false,
            isSearching: false,
            searchQuery: '',
            searchResults: [],
            resetTimeout: null
        }
    },
    computed: {
        ...mapState(useScrollStore, [
            'isScrolled',
            'scrollDirection'
        ]),
        triggerClasses () {
            return {
                'search-trigger--drawer-is-open': this.isSearchDrawerOpen
            }
        },
        drawerClasses () {
            return {
                'search-drawer--is-open': this.isSearchDrawerOpen
            }
        },
        isNoResults () {
            return this.searchQuery && !this.isSearching && this.searchResults.length === 0
        }
    },
    mounted () {
        window.addEventListener('keydown', this.handleKeydown)
    },
    beforeUnmount () {
        window.removeEventListener('keydown', this.handleKeydown)
    },
    watch: {
        isSearchDrawerOpen (isOpen) {
            // reset clear timeout
            if (this.resetTimeout) { clearTimeout(this.resetTimeout) }
            this.resetTimeout = null

            if (isOpen) {
                this.$nextTick(() => {
                    this.$refs.searchInput?.focus()
                })
            } else {
                // if we always reset on close, or if there are no results,
                // clear/reset the search
                if (this.isResetOnClose || !this.searchQuery.trim() || this.searchResults.length === 0) {
                    this.resetSearch()
                }

                // if we reset after a certain number of seconds the search
                // drawer has been closed, start the timeout
                if (Number.isInteger(this.resetSecondsAfterDrawerClosed) && this.resetSecondsAfterDrawerClosed > 0)  {
                    this.resetTimeout = setTimeout(() => {
                        this.resetSearch()
                    }, this.resetSecondsAfterDrawerClosed * 1000)
                }
            }
        }
    },
    methods: {
        handleKeydown (event) {
            // Command-K on Mac, Ctrl-K on Windows/Linux
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                // Don't trigger if user is typing in an input/textarea
                const target = event.target
                if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                    return
                }

                event.preventDefault()
                this.toggleSearchDrawer()
            }
        },
        toggleSearchDrawer () {
            this.isSearchDrawerOpen = !this.isSearchDrawerOpen
        },
        closeSearchDrawer () {
            this.isSearchDrawerOpen = false
        },
        resetSearch () {
            this.searchQuery = ''
            this.searchResults = []
        },
        onSearchInput: debounce(function () {
            if (!this.searchQuery || this.searchQuery.trim().length < 2) {
                this.searchResults = []
                this.isSearching = false
                return
            }

            this.searchProducts(this.searchQuery.trim())
        }, 300),
        searchProducts (query) {
            this.isSearching = true

            fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`)
                .then(response => response.json())
                .then(data => {
                    // Handle different possible response structures
                    const products = data.resources?.results?.products ||
                                   data.resources?.results ||
                                   data.products ||
                                   []
                    this.searchResults = products
                    this.isSearching = false
                })
                .catch(error => {
                    console.error('Search error:', error)
                    this.searchResults = []
                    this.isSearching = false
                })
        }
    },
    components: {
        SvgIconSearch,
        SvgIconX,
        SvgIconReset,
        Spinner,
        ProductThumb
    }
};
</script>
